import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";

import {
    calcReport,
    compareReport,
    deleteVariant,
    fetchVariantsByUser,
    saveVariant,
    setFavoritesForVariant,
    setError as setErrorAction,
    setCurrentVariant as setCurrentVariantAction,
    clearFilters,
    fetchReportColumns,
    clearVariant,
    clearParameters,
    clearReport,
} from "../../../reducers/reports/roleLevelSlice";

import { DIALOG_TYPES, REPORT_LEVEL_MODE } from "../../../utils/sodReports";

import { ProgressDialog, ErrorsDialog, SubmitDialog } from "../../common/dialogs";
import useReduxValue from "../../common/hooks/useReduxValue";

import { FavoritesDialog, SaveVariantDialog, DeleteVariantDialog, VariantsListBox, VariantsListDialog } from "../report-variants";
import ReportActions from "../report-actions";
import { ReportHeader } from "../report-header";

import RoleLevelReportParameters from "./role-level-report-parameters.jsx";
import RoleLevelReportResults from "./role-level-report-results.jsx";

const ConfirmSubmitDialog = ({ open, handleSubmitClick, handleCloseClick }) => {
    const intl = useIntl();
    return (
        <SubmitDialog
            open={open}
            title={""}
            message={intl.formatMessage({ id: "role-level.confirm-calc-with-empty-users-field-dialog" })}
            onCancelClick={handleCloseClick}
            buttons={[
                {
                    text: intl.formatMessage({ id: "common.submit-dialog.btn-close" }),
                    type: "close",
                    onClick: handleCloseClick,
                },
                {
                    text: intl.formatMessage({ id: "common.submit-dialog.btn-apply" }),
                    type: "apply",
                    onClick: handleSubmitClick,
                },
            ]}
        />
    );
};

const RoleLevelReportPage = () => {
    const { variantName: encodedVariantName } = useParams();
    const variantName = encodedVariantName ? decodeURIComponent(encodedVariantName) : undefined;

    const navigate = useNavigate();

    const dispatch = useDispatch();

    const [currentVariant, setCurrentVariant] = useReduxValue((state) => state.reports.roleLevel.currentVariant, setCurrentVariantAction);
    const [error, setError] = useReduxValue((state) => state.reports.roleLevel.error, setErrorAction);

    const [variantDialogType, setVariantDialogType] = useState(null);
    const [dialogVariant, setDialogVariant] = useState(null);
    const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);

    const roles = useSelector((state) => state.reports.roleLevel.roles);
    const busy = useSelector((state) => state.reports.roleLevel.busy);
    const busyType = useSelector((state) => state.reports.roleLevel.busyType);
    const variantCreated = useSelector((state) => state.reports.roleLevel.variantCreated);
    const variants = useSelector((state) => state.reports.roleLevel.variants);
    const parametersOpen = useSelector((state) => state.reports.roleLevel.parametersOpen);
    const validationErrors = useSelector((state) => state.reports.roleLevel.validationErrors);
    const reportLevelMode = useSelector((state) => state.reports.roleLevel.reportLevelMode);

    useEffect(() => {
        dispatch(fetchVariantsByUser());

        return () => {
            dispatch(clearFilters());
            dispatch(clearVariant());
            dispatch(clearParameters());
            dispatch(clearReport());
        };
    }, [dispatch]);

    useEffect(() => {
        if (currentVariant && !variantName) {
            dispatch(clearVariant());
            dispatch(clearParameters());
            dispatch(clearReport());
            return;
        }

        if (!currentVariant && !variantName) {
            return;
        }

        if (variantName && currentVariant?.name === variantName) {
            return;
        }
        if (variantName) {
            const newCurrentVariant = variants.find((variant) => variant.name === variantName);

            if (!newCurrentVariant) {
                return;
            }
            dispatch(clearReport());
            setCurrentVariant(newCurrentVariant);
        }
    }, [variantName, dispatch, variants, currentVariant, setCurrentVariant, navigate]);

    useEffect(() => {
        if (variantCreated) {
            hideVariantDialog();
        }
    }, [variantCreated]);

    const hideVariantDialog = () => {
        setDialogVariant(null);
        setVariantDialogType(null);
    };

    // Variants list dialog
    const handleShowVariantsClick = () => {
        setVariantDialogType(DIALOG_TYPES.VARIANTS);
    };

    // Delete dialog
    const handleDeleteDialogConfirmClick = () => {
        dispatch(deleteVariant(dialogVariant.name));
        hideVariantDialog();
    };

    const handleDeleteClick = (variant) => {
        setFavoritesForVariant({
            variant: dialogVariant,
            inFavorites: false,
        });
        setVariantDialogType(DIALOG_TYPES.DELETE);
        setDialogVariant(variant);
    };

    // Favorites dialog
    const handleFavoritesDialogConfirmClick = () => {
        dispatch(
            setFavoritesForVariant({
                variant: dialogVariant,
                inFavorites: !dialogVariant.inFavorites,
            })
        );

        hideVariantDialog();
    };

    const handleFavoriteClick = (variant) => {
        setVariantDialogType(DIALOG_TYPES.FAVORITES);
        setDialogVariant(variant);
    };

    // Save dialog
    const handleSaveDialogConfirmClick = async (variantName) => {
        await dispatch(saveVariant(variantName)).unwrap();
        navigate(`/report-role-level/${encodeURIComponent(variantName)}`);
    };

    const handleSaveClick = () => {
        setVariantDialogType(DIALOG_TYPES.SAVE);
    };

    const handleSubmitClick = async () => {
        if (!roles.length && reportLevelMode !== REPORT_LEVEL_MODE.CRITICAL_OBJECTS) {
            setConfirmSubmitOpen(true);
            return;
        }
        if (reportLevelMode == REPORT_LEVEL_MODE.CRITICAL_OBJECTS) {
            // dispatch(clearReport());
            await dispatch(calcReport());
            dispatch(fetchReportColumns());
            return;
        }
        dispatch(clearReport());
        dispatch(calcReport());
    };

    const handleSearchVariant = useCallback(
        (event) => {
            dispatch(fetchVariantsByUser(event.target.value));
        },
        [dispatch]
    );

    const handleVariantClick = (variant) => {
        if (variant.name === variantName) {
            setCurrentVariant(variant);
        } else {
            const encodedVariant = encodeURIComponent(variant.name);

            if (variantName) {
                navigate(`../${encodedVariant}`, { relative: "path" });
            } else {
                navigate(encodedVariant);
            }
        }

        hideVariantDialog();
    };

    const handleCompareReport = () => {
        dispatch(compareReport());
    };

    const variantsListDialogOpen = variantDialogType === DIALOG_TYPES.VARIANTS;
    const saveDialogOpen = variantDialogType === DIALOG_TYPES.SAVE;
    const deleteDialogOpen = dialogVariant && variantDialogType === DIALOG_TYPES.DELETE;
    const favoritesDialogOpen = dialogVariant && variantDialogType === DIALOG_TYPES.FAVORITES;

    return (
        <>
            <ReportHeader
                parameters={<RoleLevelReportParameters onShowVariantsClick={handleShowVariantsClick} />}
                variants={
                    <VariantsListBox
                        variants={variants}
                        onDeleteClick={handleDeleteClick}
                        onFavoriteClick={handleFavoriteClick}
                        onItemClick={handleVariantClick}
                        onSearch={handleSearchVariant}
                    />
                }
                parametersOpen={parametersOpen}
            />

            <ReportActions onSubmitClick={handleSubmitClick} onSaveClick={handleSaveClick} onReportCompare={handleCompareReport} />

            <RoleLevelReportResults />

            {saveDialogOpen && (
                <SaveVariantDialog
                    defaultName={currentVariant?.name}
                    open={saveDialogOpen}
                    validationErrors={validationErrors}
                    onSaveClick={handleSaveDialogConfirmClick}
                    onCancelClick={hideVariantDialog}
                />
            )}

            {deleteDialogOpen && (
                <DeleteVariantDialog
                    open={deleteDialogOpen}
                    variant={dialogVariant}
                    onDeleteClick={handleDeleteDialogConfirmClick}
                    onCancelClick={hideVariantDialog}
                />
            )}

            {favoritesDialogOpen && (
                <FavoritesDialog
                    open={favoritesDialogOpen}
                    variant={dialogVariant}
                    onFavoritesClick={handleFavoritesDialogConfirmClick}
                    onCancelClick={hideVariantDialog}
                />
            )}

            {variantsListDialogOpen && (
                <VariantsListDialog
                    open={variantsListDialogOpen}
                    variants={variants}
                    onDeleteClick={handleDeleteClick}
                    onFavoriteClick={handleFavoriteClick}
                    onItemClick={handleVariantClick}
                    onSearch={handleSearchVariant}
                    onClose={hideVariantDialog}
                />
            )}

            {reportLevelMode !== REPORT_LEVEL_MODE.CRITICAL_OBJECTS && (
                <ConfirmSubmitDialog
                    open={confirmSubmitOpen}
                    handleCloseClick={() => setConfirmSubmitOpen(false)}
                    handleSubmitClick={() => {
                        setConfirmSubmitOpen(false);
                        dispatch(calcReport());
                    }}
                />
            )}

            <ProgressDialog open={busy} busyType={busyType} />

            <ErrorsDialog error={error} open={error?.type === "error"} onClose={() => setError(null)} />
        </>
    );
};

ConfirmSubmitDialog.propTypes = {
    open: PropTypes.bool,
    handleCloseClick: PropTypes.func,
    handleSubmitClick: PropTypes.func,
};

export default RoleLevelReportPage;
